import {useId, useState} from 'react';
import {Check} from 'lucide-react';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Field,
  FieldError,
  FieldLabel,
} from '@tinct/web-components';
import {FieldPathByValue, FieldValues, useController, useFormContext,} from 'react-hook-form';
import {useLookupCompanies} from '@tinct/core-api';
import {ReadCompanyRef} from '@tinct/core-api-models';
import {cn} from '@tinct/core-utils';

type ControlledCompaniesSelectorProps<T extends FieldValues> = {
  name: FieldPathByValue<T, ReadCompanyRef[]>;
  label?: string;
  placeholder?: string;
  emptyMessage?: string;
  className?: string;
};

export function ControlledCompaniesSelectorInput<T extends FieldValues>(
  props: ControlledCompaniesSelectorProps<T>,
) {
  const {
    name,
    label,
    placeholder = 'Search a company...',
    emptyMessage = 'No companies found.',
    className,
  } = props;

  const [searchTerm, setSearchTerm] = useState('');
  const form = useFormContext();
  const { control } = form;
  const { field, fieldState } = useController({
    name,
    control,
  });
  const id = useId();

  const selectedCompanies = (field.value || []) as ReadCompanyRef[];

  const { data: suggestions = [], isLoading } = useLookupCompanies(
    { term: searchTerm },
    { enabled: searchTerm.length >= 0 },
  );

  const isSelected = (companyId: string) =>
    selectedCompanies.some((c) => c.id === companyId);

  const handleToggle = (company: ReadCompanyRef) => {
    if (isSelected(company.id)) {
      field.onChange(selectedCompanies.filter((c) => c.id !== company.id));
    } else {
      field.onChange([...selectedCompanies, company]);
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((word) => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <Field data-invalid={fieldState.invalid} className={className}>
      {label && <FieldLabel htmlFor={id}>{label}</FieldLabel>}
      <Command
        className="border rounded-md"
        shouldFilter={false}
      >
        <CommandInput
          id={id}
          placeholder={placeholder}
          value={searchTerm}
          onValueChange={setSearchTerm}
          onBlur={field.onBlur}
        />
        <CommandList className="h-[248px]">
          {searchTerm.length === 0 ? (
            <CommandEmpty className="py-6 text-center text-sm text-muted-foreground">
              Type at least one character to search...
            </CommandEmpty>
          ) : isLoading ? (
            <CommandEmpty className="py-6 text-center text-sm text-muted-foreground">
              Searching...
            </CommandEmpty>
          ) : suggestions.length === 0 ? (
            <CommandEmpty>{emptyMessage}</CommandEmpty>
          ) : (
            <CommandGroup heading="Suggestions">
              {suggestions.map((company) => {
                const selected = isSelected(company.id);
                return (
                  <CommandItem
                    key={company.id}
                    value={company.id}
                    onSelect={() => handleToggle(company)}
                    className={cn('gap-2 h-10', selected && 'bg-muted data-[selected=true]:bg-muted')}
                  >
                    <Avatar className="size-6 rounded-sm">
                      <AvatarImage src={company.logo?.url ?? ''} alt={company.name} />
                      <AvatarFallback className="rounded-sm text-xs">
                        {getInitials(company.name)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="flex-1 truncate">
                      {company.name}
                      {company.domain && (
                        <span className="ml-2 text-muted-foreground">
                          {company.domain}
                        </span>
                      )}
                    </span>
                    {selected && <Check className="size-4 text-foreground" />}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          )}
        </CommandList>
      </Command>
      {selectedCompanies.length > 0 && (
        <div className="mt-2 text-right text-sm text-muted-foreground">
          {selectedCompanies.length}{' '}
          {selectedCompanies.length === 1 ? 'company' : 'companies'} selected
        </div>
      )}
      {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
    </Field>
  );
}
